import styled, { css } from 'styled-components';
import {
  STEPPER_DENSITY_TOKENS,
  STEPPER_STEP_STATE_ACTIVE,
  STEPPER_STEP_STATE_COMPLETED,
  type StepperDensity,
  type StepperStepState,
} from './Stepper.constants';

export const Root = styled.ol`
  display: flex;
  align-items: flex-start;
  margin: 0;
  padding: 0;
  list-style: none;
  width: 100%;
`;

export const Item = styled.li<{ $isLast: boolean }>`
  display: flex;
  align-items: center;
  min-width: 0;
  ${({ $isLast }) =>
    $isLast
      ? css`
          flex: 0 0 auto;
        `
      : css`
          flex: 1 1 auto;
        `}
`;

export const StepButton = styled.button<{
  $density: StepperDensity;
  $clickable: boolean;
}>`
  display: inline-flex;
  align-items: center;
  border: none;
  background: transparent;
  padding: 0;
  min-width: 0;
  text-align: left;
  font: inherit;
  color: inherit;
  ${({ $density }) => css`
    gap: ${STEPPER_DENSITY_TOKENS[$density].gap};
  `}
  ${({ $clickable }) =>
    $clickable
      ? css`
          cursor: pointer;
        `
      : css`
          cursor: default;
        `}

  &:focus-visible {
    outline: none;
  }
`;

export const Indicator = styled.span<{
  $state: StepperStepState;
  $density: StepperDensity;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1.5px solid;
  font-weight: 600;
  font-size: 12.5px;
  font-variant-numeric: tabular-nums;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  ${({ $density }) => {
    const tokens = STEPPER_DENSITY_TOKENS[$density];
    return css`
      width: ${tokens.indicatorSize};
      height: ${tokens.indicatorSize};
    `;
  }}

  ${({ $state, theme: { colors = {} } }) => {
    const primary = colors.primary ?? '#C1C1FF';
    const onPrimary = colors.onPrimary ?? '#13131B';
    const muted = colors.onSurfaceVariant ?? '#C7C4D7';
    const ring = colors.outlineVariant ?? '#464555';
    if ($state === STEPPER_STEP_STATE_COMPLETED) {
      return css`
        background: ${primary};
        border-color: ${primary};
        color: ${onPrimary};
      `;
    }
    if ($state === STEPPER_STEP_STATE_ACTIVE) {
      return css`
        background: color-mix(in oklch, ${primary} 18%, transparent);
        border-color: ${primary};
        color: ${primary};
      `;
    }
    return css`
      background: transparent;
      border-color: ${ring};
      color: ${muted};
    `;
  }}

  ${StepButton}:focus-visible & {
    box-shadow: 0 0 0 2px
      ${({ theme: { colors = {} } }) => colors.primary ?? '#C1C1FF'};
  }

  & > svg {
    width: 58%;
    height: 58%;
  }
`;

export const TextCol = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const Label = styled.span<{
  $state: StepperStepState;
  $density: StepperDensity;
}>`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${({ $density }) => css`
    font-size: ${STEPPER_DENSITY_TOKENS[$density].labelFontSize};
  `}
  ${({ $state, theme: { colors = {} } }) => {
    const isMuted = $state !== STEPPER_STEP_STATE_ACTIVE;
    return css`
      font-weight: ${$state === STEPPER_STEP_STATE_ACTIVE ? 600 : 500};
      color: ${isMuted
        ? colors.onSurfaceVariant ?? '#C7C4D7'
        : colors.onSurface ?? '#E4E1ED'};
    `;
  }}
`;

export const Description = styled.span<{ $density: StepperDensity }>`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${({ $density }) => css`
    font-size: ${STEPPER_DENSITY_TOKENS[$density].descriptionFontSize};
  `}
  color: ${({ theme: { colors = {} } }) => colors.onSurfaceVariant ?? '#C7C4D7'};
`;

export const Connector = styled.span<{ $done: boolean }>`
  flex: 1 1 auto;
  height: 2px;
  margin: 0 12px;
  border-radius: 1px;
  min-width: 16px;
  ${({ $done, theme: { colors = {} } }) => {
    const primary = colors.primary ?? '#C1C1FF';
    const ring = colors.outlineVariant ?? '#464555';
    return css`
      background: ${$done
        ? `color-mix(in oklch, ${primary} 55%, transparent)`
        : ring};
    `;
  }}
`;
